import React, { useEffect, useRef, useContext, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const MapboxHeatmap = ({
  region = 'global',
  city = '',
  days = 30,
  huntId,
  businessId,
}) => {
  const { player } = useContext(AuthContext);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Helper: safely resize only when map and DOM are valid
  const safeResize = (map) => {
    if (!map || typeof map.resize !== 'function') return;
    const container = map.getContainer?.();
    const canvas = map.getCanvas?.();
    if (!container || !container.isConnected || !canvas) return;
    try {
      map.resize();
    } catch {
      /* no-op */
    }
  };

  useEffect(() => {
    let resizeObserver;
    let destroyed = false;
    let rafId = 0;

    // Abort fetch if unmounted or params change
    const ac = new AbortController();

    // Only initialize if container is available and attached
    if (!mapContainerRef.current || !mapContainerRef.current.isConnected) {
      return () => {
        destroyed = true;
        ac.abort();
      };
    }

    const initializeMap = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams({ region, city, days });
        if (businessId) params.append('businessId', businessId);

        const { data } = await axiosConn.get(
          `checkins/heatmap?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${player?.token}` },
            signal: ac.signal,
          },
        );

        const geoJson = data?.data;

        if (destroyed) return;

        if (!geoJson || !geoJson.features || geoJson.features.length === 0) {
          setError('No recent check-in data available.');
          setLoading(false);
          return;
        }

        // If an old map exists, remove and null it immediately
        if (mapRef.current) {
          try {
            mapRef.current.remove();
          } catch {}
          mapRef.current = null;
        }

        // Compute bounds defensively
        const bounds = new mapboxgl.LngLatBounds();
        for (const f of geoJson.features) {
          const coords = f?.geometry?.coordinates;
          if (
            Array.isArray(coords) &&
            coords.length === 2 &&
            Number.isFinite(coords[0]) &&
            Number.isFinite(coords[1])
          ) {
            bounds.extend(coords);
          }
        }

        // If container was detached during async fetch, bail out
        if (!mapContainerRef.current || !mapContainerRef.current.isConnected) {
          return;
        }

        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: 'mapbox://styles/mapbox/dark-v10',
          center: bounds.isEmpty() ? [-88.1, 36.05] : bounds.getCenter(),
          zoom: 11,
        });

        mapRef.current = map;

        map.once('load', () => {
          // If effect cleaned up or the ref points elsewhere, skip
          if (destroyed || mapRef.current !== map) return;

          // Fit bounds or fallback center/zoom
          if (bounds && !bounds.isEmpty()) {
            let sw = bounds.getSouthWest();
            let ne = bounds.getNorthEast();

            // Avoid degenerate bounds (same point)
            if (
              Math.abs(sw.lng - ne.lng) < 0.0002 &&
              Math.abs(sw.lat - ne.lat) < 0.0002
            ) {
              bounds.extend([sw.lng - 0.001, sw.lat - 0.001]);
              bounds.extend([ne.lng + 0.001, ne.lat + 0.001]);
              sw = bounds.getSouthWest();
              ne = bounds.getNorthEast();
            }

            if (
              Number.isFinite(sw.lng) &&
              Number.isFinite(sw.lat) &&
              Number.isFinite(ne.lng) &&
              Number.isFinite(ne.lat)
            ) {
              try {
                map.fitBounds(bounds, { padding: 120, maxZoom: 13 });
              } catch {
                map.setCenter([-88.1, 36.05]);
                map.setZoom(11);
              }
            } else {
              map.setCenter([-88.1, 36.05]);
              map.setZoom(11);
            }
          } else {
            map.setCenter([-88.1, 36.05]);
            map.setZoom(11);
          }

          // Add heatmap layer if we have data
          if (geoJson?.features?.length > 0) {
            if (!map.getSource('checkin-heatmap')) {
              map.addSource('checkin-heatmap', {
                type: 'geojson',
                data: geoJson,
              });
            }

            if (!map.getLayer('checkin-heatmap-layer')) {
              map.addLayer({
                id: 'checkin-heatmap-layer',
                type: 'heatmap',
                source: 'checkin-heatmap',
                maxzoom: 15,
                paint: {
                  'heatmap-weight': ['get', 'intensity'],
                  'heatmap-intensity': 1.5,
                  'heatmap-color': [
                    'interpolate',
                    ['linear'],
                    ['heatmap-density'],
                    0,
                    'rgba(0, 0, 255, 0)',
                    0.1,
                    'royalblue',
                    0.3,
                    'cyan',
                    0.5,
                    'lime',
                    0.7,
                    'yellow',
                    1,
                    'red',
                  ],
                  'heatmap-radius': 30,
                  'heatmap-opacity': 0.85,
                },
              });
            }
          } else {
            setError('No recent check-in data available.');
          }

          setLoading(false);
        });
      } catch (err) {
        if (ac.signal.aborted || destroyed) return;
        console.error('❌ Heatmap load error:', err);
        setError('Error loading heatmap data.');
        setLoading(false);
      }
    };

    initializeMap();

    // Resize guards (ResizeObserver)
    if (mapContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        // throttle to next frame
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => safeResize(mapRef.current));
      });
      try {
        resizeObserver.observe(mapContainerRef.current);
      } catch {}
    }

    // Visibility change (when returning to tab/page)
    const handleVisibility = () => safeResize(mapRef.current);
    document.addEventListener('visibilitychange', handleVisibility);

    // Also handle window resizes to be extra safe
    const handleWindowResize = () => safeResize(mapRef.current);
    window.addEventListener('resize', handleWindowResize, { passive: true });

    return () => {
      destroyed = true;
      ac.abort();
      cancelAnimationFrame(rafId);

      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('resize', handleWindowResize);

      try {
        resizeObserver?.disconnect();
      } catch {}

      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch {}
        // Critical: null the reference so any late callbacks will see "no map"
        mapRef.current = null;
      }
    };
  }, [region, city, days, huntId, businessId, player?.token]);

  return (
    <div style={{ width: '100%', height: '420px', borderRadius: '12px' }}>
      {loading && <div>Loading heatmap...</div>}
      {error && !loading && <div style={{ color: 'orange' }}>{error}</div>}
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default MapboxHeatmap;
